import React from "react";
import { useHistory } from "react-router";
import PropTypes from "prop-types";
import {
  Typography,
  Grid,
  Card,
  CardActions,
  IconButton,
  CardHeader,
  Box,
  Button,
  Avatar,
} from "@mui/material";
import { BsExclamationTriangleFill } from "react-icons/bs";
import { FaStethoscope } from "react-icons/fa";
import { MdClose } from "react-icons/md";

import * as API from "../api";
import ScrollToTop from "../layout/ScrollToTop";
import Flash from "../layout/Flash";
import Greeting from "./dashboard/Greeting";
import CprsList from "./dashboard/CprsList";
import * as CONST from "../Constants";
import { PatientData } from "../Contexts";
import Loading from "../../../shared/Loading";
import PcpLocationItem from "./dashboard/PcpLocationItem";
import OutOfAreaPcpForm from "./dashboard/OoaPcpForm";
import ElectedPcp from "./dashboard/ElectedPcp";

const propTypes = {
  cprs: PropTypes.array.isRequired,
  in_progress: PropTypes.object.isRequired,
  window: () => Window,
  notifications: PropTypes.array.isRequired,
  scrollToView: () => PropTypes.func.isRequired,
  children: PropTypes.object.isRequired,
};

Dashboard.prototype = propTypes;

export default function Dashboard(props) {
  const history = useHistory();
  const patient = React.useContext(PatientData);
  const [loading, setLoading] = React.useState(false);
  const [location, setLocation] = React.useState(null);
  const [showOutOfAreaElection, setShowOutOfAreaElection] = React.useState(false);
  const [flash, setFlash] = React.useState(null);

  React.useEffect(() => {
    if (!location) {
      if (patient.pcp && patient.pcp.latitude) {
        setLocation({
          lat: parseFloat(patient.pcp.latitude),
          lng: parseFloat(patient.pcp.longitude),
        });
      } else {
        setLocation({
          lat: parseFloat('36.1692'),
          lng: parseFloat('115.1406'),
        });
      }
    }
  }, [props.notifications, props.messages, props.cprs, patient.pcp, patient.pcp && patient.pcp.latitude]);

  const OoaPcpForm = () => {
    const onSubmitPcp = async (req) => {
      setLoading(true);
      let request;
      request = {
        ooa_pcp: {
          ...req,
        },
      };
      let res = await API.postToPatientSettings(request);
      if (res.error) {
        setFlash(res);
      } else {
        props.fetch.patientData();
        setFlash({
          success: CONST.FLASH.PCP_ELECTED_OUT_OF_AREA,
        });
        setLocation(null);
      }

      setTimeout(() => {
        setShowOutOfAreaElection(false);
        setLoading(false);
      }, 1500);
    };
    return (
      <Box p={1}>
        <CardActions>
          <IconButton
            sx={{ ml: "auto", mb: "auto" }}
            className="card-button "
            onClick={(e) => setShowOutOfAreaElection(false)}
            aria-label="cancel"
          >
            <MdClose />
          </IconButton>
        </CardActions>
        {loading ? (
          <Loading message="loading" />
        ) : (
          <OutOfAreaPcpForm
            values={{
              first_name:
                patient.ooa_pcp && patient.ooa_pcp.first_name ? patient.ooa_pcp.first_name : "",
              last_name:
                patient.ooa_pcp && patient.ooa_pcp.last_name ? patient.ooa_pcp.last_name : "",
              address: patient.ooa_pcp && patient.ooa_pcp.address ? patient.ooa_pcp.address : "",
              phone_number:
                patient.ooa_pcp && patient.ooa_pcp.phone_number ? patient.ooa_pcp.phone_number : "",
            }}
            onSubmit={async (vals) => onSubmitPcp(vals)}
          />
        )}
      </Box>
    );
  };
  const renderOoaChangePcpButtons = (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={6}>
        <Button
          onClick={(e) => {
            setShowOutOfAreaElection(true);
          }}
        >
          {patient.ooa_pcp && !patient.ooa_pcp.first_name
            ? "Enter PCP's Information"
            : "Update PCP"}
        </Button>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Button
          onClick={(e) => {
            history.push("/patient_portal/pcp_election");
          }}
        >
          Select a PCP
        </Button>
      </Grid>
    </Grid>
  );
  const renderOoaPcp = (
    <Box p={2}>
      <Typography variant="overline" pl={2}>
        Your PCP
      </Typography>

      <CardHeader
        sx={{ color: "#0a3a56", px: 2, pb: 2 }}
        avatar={
          <Avatar sx={{ color: "#0a3a56", bgcolor: "#f4f4f4" }}>
            <FaStethoscope style={{ fontSize: "21px" }} />
          </Avatar>
        }
        title={patient.ooa_pcp && (patient.ooa_pcp.first_name + " " + patient.ooa_pcp.last_name)}
      />

      <Box px={2} py={1}>
        {patient.ooa_pcp && patient.ooa_pcp.first_name && (
          <Typography mb={1}>{CONST.OUT_OF_AREA_CHANGE_PCP}</Typography>
        )}
        {renderOoaChangePcpButtons}
      </Box>
    </Box>
  );

  const renderInAreaPcp = (
    <ElectedPcp
      pcp={patient.pcp?.full_name ? patient.pcp.full_name : patient.pcp?.name}
      vip={patient.vip_number}
      practice={
        <PcpLocationItem
          practice={{
            ...patient.pcp,
            pcp_group: patient.pcp.pcp_group,
            name: patient.pcp.practice,
          }}
        />
      }
      actions={() => history.push("/patient_portal/pcp_election")}
    />
  );
  const renderInAreaElectPcp = (
    <Box p={3}>
      <Box fontSize="25px" color="#0faf97" mb={2}>
        {CONST.IN_AREA_PCP_ELECTION}
      </Box>
      <Button
        onClick={(e) => {
          history.push("/patient_portal/pcp_election");
        }}
      >
        Select a PCP
      </Button>
    </Box>
  );

  const renderOoaElectPcp = (
    <>
      {!showOutOfAreaElection ? (
        <Box p={3}>
          <Box fontSize="25px" color="#24a491" mb={1} fontWeight="600">
            <BsExclamationTriangleFill /> {CONST.OUT_OF_AREA_PCP_ELECTION.HEADER}
          </Box>
          <Typography mb={1.5}>{CONST.OUT_OF_AREA_PCP_ELECTION.BODY_1}</Typography>
          <Typography mb={2}>{CONST.OUT_OF_AREA_PCP_ELECTION.BODY_2}</Typography>
          {renderOoaChangePcpButtons}
        </Box>
      ) : (
        <OoaPcpForm />
      )}
    </>
  );

  return (
    <div className="dashboard">
      {flash && <Flash flash={flash} handleClose={() => setFlash(null)} />}
      <Greeting />
      <ScrollToTop>
        <div id="scroll-dashboard" />
        <Grid
          container
          direction="row"
          justifyContent="space-around"
          alignItems="center"
          className="pcp-list"
        >
          <Grid item xs={11} sm={12} className="pcp">
            <Card sx={{ my: 4, bg: "#cfffef" }}>
              {(patient.pcp && patient.pcp.name) ||
              (patient.ooa_pcp && patient.ooa_pcp.first_name) ? (
                <>
                  {patient.pcp && (patient.pcp.name || patient.pcp.full_name)
                    ? renderInAreaPcp
                    : patient.ooa_pcp.first_name &&
                      (!showOutOfAreaElection ? renderOoaPcp : renderOoaElectPcp)}
                </>
              ) : !patient.out_of_area ? (
                renderInAreaElectPcp
              ) : (
                renderOoaElectPcp
              )}
            </Card>
          </Grid>
        </Grid>
        <Box
          className="bt"
          sx={{
            mx: { md: -2, sm: 0 },
            mt: 1,
          }}
        />
        <Grid container direction="row" justifyContent="space-around" alignItems="center">
          <Grid item xs={11} sm={12} pt={1}>
            <CprsList {...props} />
          </Grid>
        </Grid>
      </ScrollToTop>
    </div>
  );
}
